import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  ArrowLeftRight, RefreshCw, Building2, Calendar, Search, X,
  ChevronDown, Download, AlertCircle, CheckCircle2, Loader2,
  BookOpen, FileText, Receipt, Settings2, Filter, TrendingUp, FileDown, FileSpreadsheet,
  Send, RotateCcw, Zap, Clock, XCircle, Link2, Link2Off, Info
} from 'lucide-react';
import { toast } from 'sonner';
import * as XLSX from 'xlsx';
import * as api from '../api';

const fmt = (n) => Number(n || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtDate = (d) => {
  if (!d) return '—';
  // Eviter le décalage timezone : parser la string ISO directement
  const s = String(d).slice(0, 10); // "2024-10-01"
  const [y, m, j] = s.split('-');
  return (j && m && y) ? `${j}/${m}/${y}` : s;
};

// ─── Barre de statut job ───────────────────────────────────────────────────────
function JobStatus({ jobId, onDone }) {
  const [job, setJob] = useState(null);
  const ref = useRef(null);

  useEffect(() => {
    if (!jobId) return;
    const poll = async () => {
      try {
        const r = await api.tcGetJobStatus(jobId);
        setJob(r.data);
        if (r.data?.status === 'completed') { onDone?.(); clearInterval(ref.current); }
        if (r.data?.status === 'error') clearInterval(ref.current);
      } catch {}
    };
    poll();
    ref.current = setInterval(poll, 2000);
    return () => clearInterval(ref.current);
  }, [jobId, onDone]);

  if (!job) return null;
  if (job.status === 'completed') return (
    <div className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm" style={{ background: '#F0FDF4', color: '#166534' }}>
      <CheckCircle2 size={14} />
      <span>Extraction terminée — {job.result?.nb_comptes ?? job.result?.nb_journaux ?? job.result?.nb_lignes ?? job.result?.nb_factures ?? 0} éléments</span>
      {job.result?.limit_used > 0 && <span className="opacity-60">(limite {job.result.limit_used})</span>}
    </div>
  );
  if (job.status === 'error') return (
    <div className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm" style={{ background: '#FEF2F2', color: '#DC2626' }}>
      <AlertCircle size={14} /><span>{job.error || 'Erreur lors de l\'extraction'}</span>
    </div>
  );
  return (
    <div className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm" style={{ background: 'var(--primary-50)', color: 'var(--primary-solid)' }}>
      <Loader2 size={14} className="animate-spin" />
      <span>{job.progress || 'Extraction en cours…'}</span>
    </div>
  );
}

// ─── Sélecteur société ─────────────────────────────────────────────────────────
function SocieteSelect({ value, societes, onChange }) {
  return (
    <select value={value || ''} onChange={e => onChange(Number(e.target.value) || null)}
      className="text-sm px-3 py-2 rounded-xl border focus:outline-none"
      style={{ borderColor: 'var(--border)', background: 'white', color: 'var(--text)' }}
      data-testid="tc-societe-select">
      <option value="">— Toutes les sociétés —</option>
      {societes.map(s => (
        <option key={s.odoo_company_id} value={s.odoo_company_id}>
          {s.societe_nom} (ID {s.odoo_company_id})
        </option>
      ))}
    </select>
  );
}

// ─── Onglet Configuration ──────────────────────────────────────────────────────
function ConfigTab({ configs, onRefresh }) {
  const [societesBedee, setSocietesBedee] = useState([]);
  const [form, setForm] = useState({ odoo_company_id: '', societe_nom: '', date_debut: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.tcGetSocietes().then(r => {
      const list = [...(r.data.societes || []), ...(r.data.agences || [])];
      setSocietesBedee(list);
    }).catch(() => {});
  }, []);

  const handleSave = async () => {
    if (!form.odoo_company_id || !form.societe_nom || !form.date_debut) {
      toast.error('Veuillez renseigner tous les champs');
      return;
    }
    setSaving(true);
    try {
      await api.tcSaveConfig({ ...form, odoo_company_id: Number(form.odoo_company_id) });
      toast.success('Configuration enregistrée');
      setForm({ odoo_company_id: '', societe_nom: '', date_debut: '' });
      onRefresh();
    } catch { toast.error('Erreur lors de la sauvegarde'); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-6">
      {/* Formulaire ajout */}
      <div className="bg-white rounded-xl border p-5" style={{ borderColor: 'var(--border)' }}>
        <h3 className="font-semibold text-sm mb-4" style={{ color: 'var(--text)',  }}>
          Configurer une société
        </h3>
        <div className="grid grid-cols-3 gap-4">
          <div>
            <label className="text-xs font-semibold uppercase tracking-wide mb-1.5 block" style={{ color: 'var(--text-muted)' }}>
              Société Bedee (avec Odoo ID)
            </label>
            <select
              value={form.odoo_company_id}
              onChange={e => {
                const s = societesBedee.find(x => String(x.odoo_company_id) === e.target.value);
                setForm(f => ({ ...f, odoo_company_id: e.target.value, societe_nom: s?.nom || f.societe_nom }));
              }}
              className="w-full text-sm px-3 py-2 rounded-xl border focus:outline-none"
              style={{ borderColor: 'var(--border)', background: 'white', color: 'var(--text)' }}>
              <option value="">— Sélectionner —</option>
              {societesBedee.map(s => (
                <option key={s.odoo_company_id} value={s.odoo_company_id}>
                  {s.nom} (ID Odoo : {s.odoo_company_id})
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-wide mb-1.5 block" style={{ color: 'var(--text-muted)' }}>Nom d'affichage</label>
            <input value={form.societe_nom} onChange={e => setForm(f => ({ ...f, societe_nom: e.target.value }))}
              placeholder="Ex : GVS Groupe" className="w-full text-sm px-3 py-2 rounded-xl border focus:outline-none"
              style={{ borderColor: 'var(--border)', color: 'var(--text)' }} />
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-wide mb-1.5 block" style={{ color: 'var(--text-muted)' }}>Date de départ extraction</label>
            <input type="date" value={form.date_debut} onChange={e => setForm(f => ({ ...f, date_debut: e.target.value }))}
              className="w-full text-sm px-3 py-2 rounded-xl border focus:outline-none"
              style={{ borderColor: 'var(--border)', color: 'var(--text)' }} />
          </div>
        </div>
        <button onClick={handleSave} disabled={saving}
          className="mt-4 px-4 py-2 rounded-xl text-sm font-semibold text-white disabled:opacity-50 transition-all hover:opacity-90"
          style={{ background: 'var(--primary-gradient)' }}>
          {saving ? 'Enregistrement…' : 'Enregistrer'}
        </button>
      </div>

      {/* Tableau des configs */}
      {configs.length > 0 && (
        <div className="bg-white rounded-xl border overflow-hidden" style={{ borderColor: 'var(--border)' }}>
          <table className="w-full text-sm">
            <thead>
              <tr style={{ background: '#FAFAFA', borderBottom: '1px solid var(--border)' }}>
                {['Société', 'ID Odoo', 'Date départ', 'Comptes', 'Écritures', 'Fac. clients', 'Fac. fourn.'].map(h => (
                  <th key={h} className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide"
                    style={{ color: 'var(--text-muted)' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {configs.map((c, i) => (
                <tr key={c.odoo_company_id} style={{ borderBottom: i < configs.length - 1 ? '1px solid var(--border-light)' : 'none' }}>
                  <td className="px-4 py-3 font-semibold" style={{ color: 'var(--text)',  }}>{c.societe_nom}</td>
                  <td className="px-4 py-3 text-xs font-mono" style={{ color: 'var(--text-muted)' }}>{c.odoo_company_id}</td>
                  <td className="px-4 py-3 text-xs" style={{ color: 'var(--text-muted)' }}>{c.date_debut || '—'}</td>
                  <td className="px-4 py-3"><span className="px-2 py-0.5 rounded-full text-xs font-semibold" style={{ background: c.nb_comptes > 0 ? '#F0FDF4' : '#F3F4F6', color: c.nb_comptes > 0 ? '#166534' : '#6B7280' }}>{c.nb_comptes}</span></td>
                  <td className="px-4 py-3"><span className="px-2 py-0.5 rounded-full text-xs font-semibold" style={{ background: c.nb_ecritures > 0 ? '#EFF6FF' : '#F3F4F6', color: c.nb_ecritures > 0 ? '#1D4ED8' : '#6B7280' }}>{c.nb_ecritures}</span></td>
                  <td className="px-4 py-3 text-xs" style={{ color: 'var(--text-muted)' }}>{c.nb_factures_clients}</td>
                  <td className="px-4 py-3 text-xs" style={{ color: 'var(--text-muted)' }}>{c.nb_factures_fournisseurs}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─── Onglet Plan Comptable ─────────────────────────────────────────────────────
function PlanComptableTab({ configs }) {
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [jobId, setJobId] = useState(null);
  const [societeId, setSocieteId] = useState(null);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const searchTimerPlan = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.tcGetPlanComptable({ odoo_company_id: societeId, search, per_page: 200 });
      setData(r.data.data || []);
      setTotal(r.data.total || 0);
    } catch { toast.error('Erreur chargement plan comptable'); }
    finally { setLoading(false); }
  }, [societeId, search]);

  useEffect(() => { load(); }, [load]);

  const handleExtract = async (limit) => {
    if (!societeId) { toast.error('Sélectionnez une société'); return; }
    try {
      const r = await api.tcExtrairePlanComptable({ odoo_company_id: societeId, limit });
      setJobId(r.data.job_id);
      toast.info(`Extraction lancée (${limit > 0 ? limit + ' lignes' : 'complet'})`);
    } catch { toast.error('Erreur lors du lancement'); }
  };

  const TYPE_LABELS = {
    asset_receivable: 'Clients', asset_cash: 'Trésorerie', asset_current: 'Actif courant',
    asset_non_current: 'Actif immo', asset_prepayments: 'Charges constatées',
    asset_fixed: 'Immobilisations', liability_payable: 'Fournisseurs',
    liability_current: 'Passif courant', liability_non_current: 'Passif LT',
    equity: 'Capitaux', equity_unaffected: 'Report', income: 'Produits',
    income_other: 'Autres produits', expense: 'Charges', expense_depreciation: 'Amortissements',
    expense_direct_cost: 'Coût direct', off_balance: 'Hors bilan',
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3 flex-wrap">
        <SocieteSelect value={societeId} societes={configs} onChange={setSocieteId} />
        <div className="flex items-center gap-2 px-3 py-2 rounded-xl border bg-white" style={{ borderColor: 'var(--border)' }}>
          <Search size={14} style={{ color: 'var(--text-muted)' }} />
          <input value={searchInput} onChange={e => { const v = e.target.value; setSearchInput(v); clearTimeout(searchTimerPlan.current); searchTimerPlan.current = setTimeout(() => setSearch(v), 300); }}
            placeholder="Code ou libellé…" className="text-sm focus:outline-none w-40"
            style={{ color: 'var(--text)' }} />
          {searchInput && <button onClick={() => { setSearchInput(''); setSearch(''); }}><X size={12} style={{ color: 'var(--text-muted)' }} /></button>}
        </div>
        <div className="flex-1" />
        <button onClick={() => handleExtract(100)} data-testid="tc-extract-pc-100"
          className="flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-semibold border transition-all hover:bg-gray-50"
          style={{ borderColor: 'var(--border)', color: 'var(--text)' }}>
          <Download size={13} />Extraire (100)
        </button>
        <button onClick={() => handleExtract(0)} data-testid="tc-extract-pc-all"
          className="flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-semibold text-white transition-all hover:opacity-90"
          style={{ background: 'var(--primary-gradient)' }}>
          <RefreshCw size={13} />Sync complète
        </button>
      </div>
      {jobId && <JobStatus jobId={jobId} onDone={() => { setJobId(null); load(); }} />}
      <div className="text-xs" style={{ color: 'var(--text-muted)' }}>{total} compte(s) en base</div>
      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="animate-spin" size={24} style={{ color: 'var(--primary-solid)' }} /></div>
      ) : (
        <div className="bg-white rounded-xl border overflow-hidden" style={{ borderColor: 'var(--border)' }}>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr style={{ background: '#FAFAFA', borderBottom: '1px solid var(--border)' }}>
                  {['Code', 'Libellé du compte', 'Type', 'Statut'].map(h => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide"
                      style={{ color: 'var(--text-muted)' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.length === 0 ? (
                  <tr><td colSpan={4} className="px-4 py-12 text-center text-sm" style={{ color: 'var(--text-muted)' }}>
                    Aucun compte — lancez une extraction
                  </td></tr>
                ) : data.map((c, i) => (
                  <tr key={c.tc_pc_id} style={{ borderBottom: i < data.length - 1 ? '1px solid var(--border-light)' : 'none' }}>
                    <td className="px-4 py-2.5 font-mono text-sm font-semibold" style={{ color: 'var(--primary-solid)' }}>{c.code}</td>
                    <td className="px-4 py-2.5" style={{ color: 'var(--text)' }}>{c.nom}</td>
                    <td className="px-4 py-2.5">
                      <span className="px-2 py-0.5 rounded-full text-xs" style={{ background: '#F3F4F6', color: '#6B7280' }}>
                        {TYPE_LABELS[c.type] || c.type}
                      </span>
                    </td>
                    <td className="px-4 py-2.5">
                      {c.deprecated
                        ? <span className="px-2 py-0.5 rounded-full text-xs" style={{ background: '#FEF2F2', color: '#DC2626' }}>Obsolète</span>
                        : <span className="px-2 py-0.5 rounded-full text-xs" style={{ background: '#F0FDF4', color: '#166534' }}>Actif</span>
                      }
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Onglet Journaux ──────────────────────────────────────────────────────────
const TYPE_JOUR_LABELS = {
  sale:     { label: 'Ventes',   bg: '#EFF6FF', color: '#1D4ED8' },
  purchase: { label: 'Achats',   bg: '#FFF7ED', color: '#C2410C' },
  cash:     { label: 'Caisse',   bg: '#F0FDF4', color: '#15803D' },
  bank:     { label: 'Banque',   bg: '#F0FDF4', color: '#0F766E' },
  general:  { label: 'OD',       bg: '#F5F3FF', color: '#6D28D9' },
};

function JournauxTab({ configs }) {
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [jobId, setJobId] = useState(null);
  const [societeId, setSocieteId] = useState(null);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const searchTimerJrn = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.tcGetJournaux({ odoo_company_id: societeId, search });
      setData(r.data.data || []);
      setTotal(r.data.total || 0);
    } catch { toast.error('Erreur chargement journaux'); }
    finally { setLoading(false); }
  }, [societeId, search]);

  useEffect(() => { load(); }, [load]);

  const handleExtract = async () => {
    if (!societeId) { toast.error('Sélectionnez une société'); return; }
    try {
      const r = await api.tcExtraireJournaux({ odoo_company_id: societeId });
      setJobId(r.data.job_id);
      toast.info('Synchronisation des journaux lancée');
    } catch { toast.error('Erreur lors du lancement'); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3 flex-wrap">
        <SocieteSelect value={societeId} societes={configs} onChange={setSocieteId} />
        <div className="flex items-center gap-2 px-3 py-2 rounded-xl border bg-white" style={{ borderColor: 'var(--border)' }}>
          <Search size={14} style={{ color: 'var(--text-muted)' }} />
          <input value={searchInput} onChange={e => { const v = e.target.value; setSearchInput(v); clearTimeout(searchTimerJrn.current); searchTimerJrn.current = setTimeout(() => setSearch(v), 300); }}
            placeholder="Code ou libellé…" className="text-sm focus:outline-none w-40"
            style={{ color: 'var(--text)' }} />
          {searchInput && <button onClick={() => { setSearchInput(''); setSearch(''); }}><X size={12} style={{ color: 'var(--text-muted)' }} /></button>}
        </div>
        <div className="flex-1" />
        <button onClick={handleExtract} data-testid="tc-extract-journaux"
          className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold text-white transition-all hover:opacity-90"
          style={{ background: 'var(--primary-gradient)' }}>
          <RefreshCw size={13} />Synchroniser les journaux
        </button>
      </div>
      {jobId && <JobStatus jobId={jobId} onDone={() => { setJobId(null); load(); }} />}
      <div className="text-xs" style={{ color: 'var(--text-muted)' }}>{total} journal/journaux en base</div>
      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="animate-spin" size={24} style={{ color: 'var(--primary-solid)' }} /></div>
      ) : data.length === 0 ? (
        <div className="bg-white rounded-xl border p-12 text-center" style={{ borderColor: 'var(--border)' }}>
          <BookOpen size={36} className="mx-auto mb-3" style={{ color: 'var(--text-disabled)' }} />
          <p className="text-sm" style={{ color: 'var(--text-muted)' }}>Aucun journal — cliquez sur "Synchroniser"</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border overflow-hidden" style={{ borderColor: 'var(--border)' }}>
          <table className="w-full text-sm">
            <thead>
              <tr style={{ background: '#FAFAFA', borderBottom: '1px solid var(--border)' }}>
                {['Code', 'Libellé', 'Type'].map(h => (
                  <th key={h} className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide"
                    style={{ color: 'var(--text-muted)' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.map((j, i) => {
                const t = TYPE_JOUR_LABELS[j.type] || { label: j.type, bg: '#F3F4F6', color: '#6B7280' };
                return (
                  <tr key={j.tc_jour_id} style={{ borderBottom: i < data.length - 1 ? '1px solid var(--border-light)' : 'none' }}>
                    <td className="px-4 py-3 font-mono font-bold text-sm" style={{ color: 'var(--primary-solid)' }}>{j.code}</td>
                    <td className="px-4 py-3 font-semibold text-sm" style={{ color: 'var(--text)' }}>{j.nom}</td>
                    <td className="px-4 py-3">
                      <span className="px-2 py-0.5 rounded-full text-xs font-semibold" style={{ background: t.bg, color: t.color }}>{t.label}</span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─── Onglet Grand Livre ────────────────────────────────────────────────────────
function GrandLivreTab({ configs }) {
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [jobId, setJobId] = useState(null);
  const [societeId, setSocieteId] = useState(null);
  const [journaux, setJournaux] = useState([]);
  const [filters, setFilters] = useState({ compte_code: '', journal_nom: '', date_debut: '', date_fin: '', search: '' });
  const [searchInput, setSearchInput] = useState('');
  const searchTimerGL = useRef(null);
  const [expanded, setExpanded] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.tcGetEcritures({ odoo_company_id: societeId, ...filters, per_page: 500 });
      setData(r.data.data || []);
      setTotal(r.data.total || 0);
      setJournaux(r.data.journaux || []);
    } catch { toast.error('Erreur chargement écritures'); }
    finally { setLoading(false); }
  }, [societeId, filters]);

  useEffect(() => { load(); }, [load]);

  const handleExtract = async (limit) => {
    if (!societeId) { toast.error('Sélectionnez une société'); return; }
    try {
      const r = await api.tcExtraireEcritures({ odoo_company_id: societeId, limit, ...filters });
      setJobId(r.data.job_id);
    } catch { toast.error('Erreur lors du lancement'); }
  };

  // Grouper par compte
  const grouped = {};
  data.forEach(l => {
    const k = `${l.compte_code}___${l.compte_nom}`;
    if (!grouped[k]) grouped[k] = { code: l.compte_code, nom: l.compte_nom, lignes: [] };
    grouped[k].lignes.push(l);
  });

  const handleExportExcel = () => {
    if (data.length === 0) { toast.error('Aucune donnée à exporter'); return; }

    const societe = configs.find(c => c.odoo_company_id === societeId);
    const nomSociete = societe?.societe_nom || 'Grand Livre';
    const rows = [];

    // En-tête informatif
    rows.push(['Grand Livre', nomSociete, '', '', '', '', '', '', '', '', '']);
    if (filters.date_debut || filters.date_fin) {
      rows.push([`Période : ${filters.date_debut || '…'} → ${filters.date_fin || '…'}`, '', '', '', '', '', '', '', '', '', '']);
    }
    rows.push([`Exporté le : ${new Date().toLocaleDateString('fr-FR')}`, '', '', '', '', '', '', '', '', '', '']);
    rows.push([]); // ligne vide

    // En-têtes colonnes
    rows.push(['Code compte', 'Libellé du compte', 'Date', 'Journal', 'Pièce', 'Référence', 'Libellé écriture', 'Tiers', 'Débit', 'Crédit', 'Solde progressif', 'Lettré']);

    const fmtNum = (n) => Number(n || 0);
    const fmtD = (d) => {
      if (!d) return '';
      const s = String(d).slice(0, 10);
      const [y, m, j] = s.split('-');
      return (j && m && y) ? `${j}/${m}/${y}` : s;
    };

    // Lignes par compte avec sous-totaux
    Object.entries(grouped).sort(([a], [b]) => a.localeCompare(b)).forEach(([, groupe]) => {
      let soldeRun = 0;
      groupe.lignes.forEach(l => {
        soldeRun += l.debit - l.credit;
        rows.push([
          groupe.code,
          groupe.nom,
          fmtD(l.date),
          l.journal_nom,
          l.move_nom,
          l.ref || '',
          l.libelle,
          l.partner_nom || '',
          fmtNum(l.debit),
          fmtNum(l.credit),
          fmtNum(soldeRun),
          l.lettre ? 'Oui' : '',
        ]);
      });
      // Sous-total du compte
      const tD = groupe.lignes.reduce((s, l) => s + l.debit, 0);
      const tC = groupe.lignes.reduce((s, l) => s + l.credit, 0);
      rows.push([
        `TOTAL ${groupe.code}`, groupe.nom, '', '', '', '', '', '',
        fmtNum(tD), fmtNum(tC), fmtNum(tD - tC), '',
      ]);
      rows.push([]); // séparateur
    });

    const ws = XLSX.utils.aoa_to_sheet(rows);

    // Largeurs de colonnes
    ws['!cols'] = [
      { wch: 12 }, { wch: 35 }, { wch: 12 }, { wch: 10 }, { wch: 14 },
      { wch: 12 }, { wch: 40 }, { wch: 30 }, { wch: 13 }, { wch: 13 }, { wch: 16 }, { wch: 8 },
    ];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Grand Livre');

    const dateStr = new Date().toISOString().slice(0, 10);
    XLSX.writeFile(wb, `grand_livre_${nomSociete.replace(/\s+/g, '_')}_${dateStr}.xlsx`);
    toast.success(`Export Excel : ${data.length} ligne(s)`);
  };

  return (
    <div className="space-y-4">
      {/* Filtres */}
      <div className="flex items-center gap-3 flex-wrap">
        <SocieteSelect value={societeId} societes={configs} onChange={setSocieteId} />
        <select value={filters.journal_nom} onChange={e => setFilters(f => ({ ...f, journal_nom: e.target.value }))}
          className="text-sm px-3 py-2 rounded-xl border bg-white focus:outline-none" style={{ borderColor: 'var(--border)', color: 'var(--text)' }}>
          <option value="">Tous les journaux</option>
          {journaux.map(j => <option key={j} value={j}>{j}</option>)}
        </select>
        <input type="date" value={filters.date_debut} onChange={e => setFilters(f => ({ ...f, date_debut: e.target.value }))}
          className="text-sm px-3 py-2 rounded-xl border bg-white focus:outline-none" style={{ borderColor: 'var(--border)', color: 'var(--text)' }} />
        <span className="text-xs" style={{ color: 'var(--text-muted)' }}>→</span>
        <input type="date" value={filters.date_fin} onChange={e => setFilters(f => ({ ...f, date_fin: e.target.value }))}
          className="text-sm px-3 py-2 rounded-xl border bg-white focus:outline-none" style={{ borderColor: 'var(--border)', color: 'var(--text)' }} />
        <div className="flex items-center gap-2 px-3 py-2 rounded-xl border bg-white" style={{ borderColor: 'var(--border)' }}>
          <Search size={14} style={{ color: 'var(--text-muted)' }} />
          <input value={searchInput} onChange={e => { const v = e.target.value; setSearchInput(v); clearTimeout(searchTimerGL.current); searchTimerGL.current = setTimeout(() => setFilters(f => ({ ...f, search: v })), 300); }}
            placeholder="Tiers, libellé, réf…" className="text-sm focus:outline-none w-36" style={{ color: 'var(--text)' }} />
        </div>
        <div className="flex-1" />
        <button onClick={() => handleExtract(100)}
          className="flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-semibold border transition-all hover:bg-gray-50"
          style={{ borderColor: 'var(--border)', color: 'var(--text)' }}>
          <Download size={13} />Extraire (100)
        </button>
        <button onClick={() => handleExtract(0)}
          className="flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-semibold text-white transition-all hover:opacity-90"
          style={{ background: 'var(--primary-gradient)' }}>
          <RefreshCw size={13} />Sync complète
        </button>
      </div>
      {jobId && <JobStatus jobId={jobId} onDone={() => { setJobId(null); load(); }} />}
      <div className="flex items-center justify-between">
        <div className="text-xs" style={{ color: 'var(--text-muted)' }}>{total} ligne(s) — {Object.keys(grouped).length} compte(s)</div>
        {data.length > 0 && (
          <button onClick={handleExportExcel} data-testid="gl-export-excel"
            className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-semibold border transition-all hover:bg-green-50"
            style={{ borderColor: '#BBF7D0', color: '#16A34A', background: '#F0FDF4' }}>
            <Download size={12} />Exporter en Excel
          </button>
        )}
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="animate-spin" size={24} style={{ color: 'var(--primary-solid)' }} /></div>
      ) : Object.keys(grouped).length === 0 ? (
        <div className="bg-white rounded-xl border p-12 text-center" style={{ borderColor: 'var(--border)' }}>
          <BookOpen size={36} className="mx-auto mb-3" style={{ color: 'var(--text-disabled)' }} />
          <p className="text-sm" style={{ color: 'var(--text-muted)' }}>Aucune écriture — lancez une extraction</p>
        </div>
      ) : (
        <div className="space-y-2">
          {Object.entries(grouped).sort(([a], [b]) => a.localeCompare(b)).map(([key, groupe]) => {
            const isOpen = expanded[key] !== false;
            const totalDebit = groupe.lignes.reduce((s, l) => s + l.debit, 0);
            const totalCredit = groupe.lignes.reduce((s, l) => s + l.credit, 0);
            const solde = totalDebit - totalCredit;
            let soldeRun = 0;
            return (
              <div key={key} className="bg-white rounded-xl border overflow-hidden" style={{ borderColor: 'var(--border)' }}>
                {/* En-tête du compte */}
                <div className="flex items-center gap-3 px-4 py-3 cursor-pointer select-none"
                  style={{ background: '#F8F9FB', borderBottom: isOpen ? '1px solid var(--border)' : 'none' }}
                  onClick={() => setExpanded(e => ({ ...e, [key]: !isOpen }))}>
                  <span className="font-mono font-bold text-sm" style={{ color: 'var(--primary-solid)', minWidth: 70 }}>{groupe.code}</span>
                  <span className="font-semibold text-sm flex-1" style={{ color: 'var(--text)',  }}>{groupe.nom}</span>
                  <span className="text-xs px-2 py-0.5 rounded-full" style={{ background: '#EFF6FF', color: '#1D4ED8' }}>{groupe.lignes.length} écriture(s)</span>
                  <div className="flex gap-6 text-xs font-semibold" style={{ color: 'var(--text-muted)' }}>
                    <span>D : <span style={{ color: '#166534' }}>{fmt(totalDebit)}</span></span>
                    <span>C : <span style={{ color: '#DC2626' }}>{fmt(totalCredit)}</span></span>
                    <span>Solde : <span style={{ color: solde >= 0 ? '#166534' : '#DC2626', fontWeight: 700 }}>{fmt(Math.abs(solde))} {solde >= 0 ? 'D' : 'C'}</span></span>
                  </div>
                  <ChevronDown size={14} style={{ color: 'var(--text-muted)', transform: isOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }} />
                </div>
                {/* Lignes du compte */}
                {isOpen && (
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead>
                        <tr style={{ background: '#FAFAFA', borderBottom: '1px solid var(--border-light)' }}>
                          {['Date', 'Journal', 'Pièce', 'Réf', 'Libellé', 'Tiers', 'Débit', 'Crédit', 'Solde', 'L'].map(h => (
                            <th key={h} className="px-3 py-2 text-left font-semibold uppercase tracking-wide"
                              style={{ color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {groupe.lignes.map((l, idx) => {
                          soldeRun += l.debit - l.credit;
                          return (
                            <tr key={l.tc_ecr_id}
                              style={{ borderBottom: idx < groupe.lignes.length - 1 ? '1px solid var(--border-light)' : 'none' }}>
                              <td className="px-3 py-2 whitespace-nowrap" style={{ color: 'var(--text-muted)' }}>{fmtDate(l.date)}</td>
                              <td className="px-3 py-2 whitespace-nowrap">
                                <span className="px-1.5 py-0.5 rounded text-xs" style={{ background: '#F3F4F6', color: '#6B7280' }}>{l.journal_nom}</span>
                              </td>
                              <td className="px-3 py-2 font-mono font-semibold whitespace-nowrap" style={{ color: 'var(--text)' }}>{l.move_nom}</td>
                              <td className="px-3 py-2 whitespace-nowrap" style={{ color: 'var(--text-muted)' }}>{l.ref || '—'}</td>
                              <td className="px-3 py-2 max-w-xs truncate" style={{ color: 'var(--text)' }}>{l.libelle}</td>
                              <td className="px-3 py-2 whitespace-nowrap" style={{ color: 'var(--text-muted)' }}>{l.partner_nom || '—'}</td>
                              <td className="px-3 py-2 text-right font-semibold whitespace-nowrap" style={{ color: l.debit > 0 ? '#166534' : 'transparent' }}>
                                {l.debit > 0 ? fmt(l.debit) : ''}
                              </td>
                              <td className="px-3 py-2 text-right font-semibold whitespace-nowrap" style={{ color: l.credit > 0 ? '#DC2626' : 'transparent' }}>
                                {l.credit > 0 ? fmt(l.credit) : ''}
                              </td>
                              <td className="px-3 py-2 text-right font-bold whitespace-nowrap" style={{ color: soldeRun >= 0 ? '#166534' : '#DC2626' }}>
                                {fmt(Math.abs(soldeRun))} {soldeRun >= 0 ? 'D' : 'C'}
                              </td>
                              <td className="px-3 py-2 text-center">
                                {l.lettre && <CheckCircle2 size={12} style={{ color: '#166534' }} />}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── Onglet Factures ──────────────────────────────────────────────────────────
function FacturesTab({ configs, moveType }) {
  const isClient = moveType === 'out_invoice';
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [jobId, setJobId] = useState(null);
  const [societeId, setSocieteId] = useState(null);
  const [filters, setFilters] = useState({ search: '', state: '', date_debut: '', date_fin: '' });
  const [searchInput, setSearchInput] = useState('');
  const searchTimerFac = useRef(null);
  const [downloadingId, setDownloadingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.tcGetFactures({ odoo_company_id: societeId, move_type: moveType, ...filters, per_page: 100 });
      setData(r.data.data || []);
      setTotal(r.data.total || 0);
    } catch { toast.error('Erreur chargement factures'); }
    finally { setLoading(false); }
  }, [societeId, moveType, filters]);

  useEffect(() => { load(); }, [load]);

  const handleExtract = async (limit) => {
    if (!societeId) { toast.error('Sélectionnez une société'); return; }
    try {
      const r = await api.tcExtraireFactures({ odoo_company_id: societeId, move_type: moveType, limit });
      setJobId(r.data.job_id);
    } catch { toast.error('Erreur lors du lancement'); }
  };

  const handleDownloadPdf = async (fac) => {
    setDownloadingId(fac.tc_fac_id);
    try {
      const r = await api.tcGetFacturePdf(fac.tc_fac_id);
      const blob = new Blob([r.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${(fac.name || 'facture').replace(/\//g, '-')}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      let msg = 'Erreur lors du téléchargement du PDF';
      try {
        // responseType arraybuffer → err.response.data est un ArrayBuffer, pas du JSON
        if (err?.response?.data instanceof ArrayBuffer) {
          const text = new TextDecoder().decode(err.response.data);
          const json = JSON.parse(text);
          msg = json.detail || json.message || text;
        } else if (err?.response?.data && typeof err.response.data === 'object') {
          msg = err.response.data.detail || err.response.data.message || JSON.stringify(err.response.data);
        } else if (typeof err?.response?.data === 'string') {
          msg = err.response.data;
        }
      } catch { /* ignore */ }
      toast.error(msg);
    } finally {
      setDownloadingId(null);
    }
  };

  const STATE_MAP = {
    posted: { label: 'Validée', bg: '#F0FDF4', color: '#166534' },
    draft:  { label: 'Brouillon', bg: '#FEF9C3', color: '#92400E' },
    cancel: { label: 'Annulée', bg: '#FEF2F2', color: '#DC2626' },
  };
  const PAY_MAP = {
    paid: { label: 'Payée', bg: '#F0FDF4', color: '#166534' },
    partial: { label: 'Partielle', bg: '#FFF7ED', color: '#C2410C' },
    not_paid: { label: 'Impayée', bg: '#FEF2F2', color: '#DC2626' },
    reversed: { label: 'Avoir', bg: '#F5F3FF', color: '#7C3AED' },
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3 flex-wrap">
        <SocieteSelect value={societeId} societes={configs} onChange={setSocieteId} />
        <select value={filters.state} onChange={e => setFilters(f => ({ ...f, state: e.target.value }))}
          className="text-sm px-3 py-2 rounded-xl border bg-white focus:outline-none" style={{ borderColor: 'var(--border)', color: 'var(--text)' }}>
          <option value="">Tous statuts</option>
          <option value="posted">Validées</option>
          <option value="draft">Brouillons</option>
          <option value="cancel">Annulées</option>
        </select>
        <input type="date" value={filters.date_debut} onChange={e => setFilters(f => ({ ...f, date_debut: e.target.value }))}
          className="text-sm px-3 py-2 rounded-xl border bg-white focus:outline-none" style={{ borderColor: 'var(--border)', color: 'var(--text)' }} />
        <span className="text-xs" style={{ color: 'var(--text-muted)' }}>→</span>
        <input type="date" value={filters.date_fin} onChange={e => setFilters(f => ({ ...f, date_fin: e.target.value }))}
          className="text-sm px-3 py-2 rounded-xl border bg-white focus:outline-none" style={{ borderColor: 'var(--border)', color: 'var(--text)' }} />
        <div className="flex items-center gap-2 px-3 py-2 rounded-xl border bg-white" style={{ borderColor: 'var(--border)' }}>
          <Search size={14} style={{ color: 'var(--text-muted)' }} />
          <input value={searchInput} onChange={e => { const v = e.target.value; setSearchInput(v); clearTimeout(searchTimerFac.current); searchTimerFac.current = setTimeout(() => setFilters(f => ({ ...f, search: v })), 300); }}
            placeholder="N°, tiers, réf…" className="text-sm focus:outline-none w-36" style={{ color: 'var(--text)' }} />
        </div>
        <div className="flex-1" />
        <button onClick={() => handleExtract(100)}
          className="flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-semibold border transition-all hover:bg-gray-50"
          style={{ borderColor: 'var(--border)', color: 'var(--text)' }}>
          <Download size={13} />Extraire (100)
        </button>
        <button onClick={() => handleExtract(0)}
          className="flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-semibold text-white transition-all hover:opacity-90"
          style={{ background: 'var(--primary-gradient)' }}>
          <RefreshCw size={13} />Sync complète
        </button>
      </div>
      {jobId && <JobStatus jobId={jobId} onDone={() => { setJobId(null); load(); }} />}
      <div className="text-xs" style={{ color: 'var(--text-muted)' }}>{total} facture(s) en base</div>
      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="animate-spin" size={24} style={{ color: 'var(--primary-solid)' }} /></div>
      ) : (
        <div className="bg-white rounded-xl border overflow-hidden" style={{ borderColor: 'var(--border)' }}>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr style={{ background: '#FAFAFA', borderBottom: '1px solid var(--border)' }}>
                  {['N° Facture', 'Tiers', 'Date', 'Échéance', 'HT', 'TVA', 'TTC', 'Statut', 'Règlement', 'PDF'].map(h => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide whitespace-nowrap"
                      style={{ color: 'var(--text-muted)' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.length === 0 ? (
                  <tr><td colSpan={10} className="px-4 py-12 text-center text-sm" style={{ color: 'var(--text-muted)' }}>
                    Aucune facture — lancez une extraction
                  </td></tr>
                ) : data.map((f, i) => {
                  const st = STATE_MAP[f.state] || { label: f.state, bg: '#F3F4F6', color: '#6B7280' };
                  const py = PAY_MAP[f.payment_state] || { label: f.payment_state, bg: '#F3F4F6', color: '#6B7280' };
                  const isDownloading = downloadingId === f.tc_fac_id;
                  return (
                    <tr key={f.tc_fac_id} style={{ borderBottom: i < data.length - 1 ? '1px solid var(--border-light)' : 'none' }}>
                      <td className="px-4 py-2.5 font-mono font-semibold text-xs" style={{ color: 'var(--primary-solid)' }}>{f.name}</td>
                      <td className="px-4 py-2.5 font-semibold text-xs max-w-xs truncate" style={{ color: 'var(--text)' }}>{f.partner_nom}</td>
                      <td className="px-4 py-2.5 text-xs whitespace-nowrap" style={{ color: 'var(--text-muted)' }}>{fmtDate(f.invoice_date)}</td>
                      <td className="px-4 py-2.5 text-xs whitespace-nowrap" style={{ color: 'var(--text-muted)' }}>{fmtDate(f.invoice_date_due)}</td>
                      <td className="px-4 py-2.5 text-xs text-right font-semibold whitespace-nowrap" style={{ color: 'var(--text)' }}>{fmt(f.amount_untaxed)}</td>
                      <td className="px-4 py-2.5 text-xs text-right whitespace-nowrap" style={{ color: 'var(--text-muted)' }}>{fmt(f.amount_tax)}</td>
                      <td className="px-4 py-2.5 text-xs text-right font-bold whitespace-nowrap" style={{ color: 'var(--text)' }}>{fmt(f.amount_total)} {f.currency_nom}</td>
                      <td className="px-4 py-2.5">
                        <span className="px-2 py-0.5 rounded-full text-xs font-semibold" style={{ background: st.bg, color: st.color }}>{st.label}</span>
                      </td>
                      <td className="px-4 py-2.5">
                        <span className="px-2 py-0.5 rounded-full text-xs font-semibold" style={{ background: py.bg, color: py.color }}>{py.label}</span>
                      </td>
                      <td className="px-4 py-2.5">
                        <button
                          data-testid={`tc-fac-pdf-${f.tc_fac_id}`}
                          onClick={() => handleDownloadPdf(f)}
                          disabled={isDownloading}
                          title="Télécharger le PDF depuis Odoo"
                          className="p-1.5 rounded-lg transition-all hover:bg-blue-50 disabled:opacity-40"
                          style={{ color: isDownloading ? 'var(--text-disabled)' : '#1D4ED8' }}>
                          {isDownloading
                            ? <Loader2 size={14} className="animate-spin" />
                            : <FileDown size={14} />}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Onglet Mapping Journaux ───────────────────────────────────────────────────
function MappingJournauxTab({ configs }) {
  const [societeId, setSocieteId] = useState(null);
  const [loading, setLoading]     = useState(false);
  const [saving, setSaving]       = useState(false);
  const [odooJournaux, setOdooJournaux] = useState([]);
  const [plJournaux, setPlJournaux]     = useState([]);
  const [localMapping, setLocalMapping] = useState({});   // { odoo_nom: pl_id | '' }
  const [savedMapping, setSavedMapping] = useState({});   // snapshot pour détecter changements

  // Auto-sélection de la première société au montage (résout la perte d'état au changement d'onglet)
  useEffect(() => {
    if (!societeId && configs.length > 0) {
      setSocieteId(configs[0].odoo_company_id);
    }
  }, [configs]); // eslint-disable-line react-hooks/exhaustive-deps

  const load = useCallback(async () => {
    if (!societeId) { setOdooJournaux([]); setPlJournaux([]); setLocalMapping({}); return; }
    setLoading(true);
    try {
      const r = await api.tcGetJournalMapping({ odoo_company_id: societeId });
      const { mapping, odoo_journaux, pl_journaux } = r.data;
      setOdooJournaux(odoo_journaux || []);
      setPlJournaux(pl_journaux || []);
      // Normaliser les IDs en string pour comparaison facile
      const norm = {};
      for (const [k, v] of Object.entries(mapping || {})) norm[k] = v ? String(v) : '';
      setLocalMapping(norm);
      setSavedMapping(norm);
    } catch { toast.error('Erreur de chargement du mapping'); }
    finally { setLoading(false); }
  }, [societeId]);

  useEffect(() => { load(); }, [load]);

  const setRow = (odooNom, plId) =>
    setLocalMapping(m => ({ ...m, [odooNom]: plId }));

  const resetRow = (odooNom) =>
    setLocalMapping(m => { const c = { ...m }; delete c[odooNom]; return c; });

  const handleSave = async () => {
    if (!societeId) return;
    setSaving(true);
    try {
      // Convertir les IDs en int (PennyLane attend des entiers)
      const payload = {};
      for (const [k, v] of Object.entries(localMapping)) {
        if (v) payload[k] = parseInt(v, 10);
      }
      await api.tcSaveJournalMapping({ odoo_company_id: societeId, mapping: payload });
      setSavedMapping({ ...localMapping });
      toast.success(`Mapping enregistré — ${Object.keys(payload).length} correspondance(s)`);
    } catch { toast.error('Erreur lors de la sauvegarde'); }
    finally { setSaving(false); }
  };

  // Ensemble des IDs PL déjà attribués (pour filtrage)
  const usedPlIds = new Set(Object.values(localMapping).filter(Boolean));

  // Détermination automatique du match PL pour un journal Odoo (si pas de mapping manuel)
  const autoMatch = (odooNom) => {
    const up = odooNom.toUpperCase();
    return plJournaux.find(j => {
      if (j.code?.toUpperCase() === up) return true;
      const lbl = (j.label || j.name || '').toUpperCase();
      return up.includes(lbl) || lbl.includes(up);
    });
  };

  const isDirty = JSON.stringify(localMapping) !== JSON.stringify(savedMapping);

  const PL_TYPE_LABEL = { bank: 'Banque', cash: 'Caisse', sale: 'Vente', purchase: 'Achat', misc: 'Divers', general: 'Général' };

  return (
    <div className="space-y-4">
      {/* Sélecteur société */}
      <div className="flex items-center gap-3 flex-wrap">
        <SocieteSelect value={societeId} societes={configs} onChange={v => { setSocieteId(v); }} />
        <div className="flex-1" />
        {isDirty && (
          <button onClick={handleSave} disabled={saving} data-testid="mapping-save-btn"
            className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold text-white transition-all hover:opacity-90 disabled:opacity-50"
            style={{ background: 'var(--primary-gradient)' }}>
            {saving ? <Loader2 size={13} className="animate-spin" /> : <Link2 size={13} />}
            Enregistrer le mapping
          </button>
        )}
        {!isDirty && Object.keys(savedMapping).length > 0 && (
          <span className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full"
            style={{ background: '#F0FDF4', color: '#166534' }}>
            <CheckCircle2 size={12} /> {Object.keys(savedMapping).length} mapping(s) actif(s)
          </span>
        )}
      </div>

      {!societeId ? (
        <div className="bg-white rounded-xl border p-12 text-center" style={{ borderColor: 'var(--border)' }}>
          <Link2 size={36} className="mx-auto mb-3" style={{ color: 'var(--text-disabled)' }} />
          <p className="text-sm" style={{ color: 'var(--text-muted)' }}>Sélectionnez une société pour configurer le mapping</p>
        </div>
      ) : loading ? (
        <div className="flex justify-center py-16"><Loader2 className="animate-spin" size={24} style={{ color: 'var(--primary-solid)' }} /></div>
      ) : odooJournaux.length === 0 ? (
        <div className="bg-white rounded-xl border p-12 text-center" style={{ borderColor: 'var(--border)' }}>
          <Info size={36} className="mx-auto mb-3" style={{ color: 'var(--text-disabled)' }} />
          <p className="text-sm" style={{ color: 'var(--text-muted)' }}>Aucun journal trouvé — extrayez d'abord les écritures</p>
        </div>
      ) : (
        <>
          {/* Légende */}
          <div className="flex items-center gap-2 px-3 py-2 rounded-lg text-xs"
            style={{ background: '#FFFBEB', color: '#92400E', border: '1px solid #FDE68A' }}>
            <Info size={12} />
            Le mapping manuel est prioritaire sur la correspondance automatique. Laissez vide pour utiliser la détection automatique.
            {plJournaux.length === 0 && <span className="ml-2 font-semibold">⚠ Journaux PennyLane indisponibles — vérifiez votre clé API.</span>}
          </div>

          {/* Tableau */}
          <div className="bg-white rounded-xl border overflow-hidden" style={{ borderColor: 'var(--border)' }}>
            <table className="w-full text-sm">
              <thead>
                <tr style={{ background: '#FAFAFA', borderBottom: '1px solid var(--border)' }}>
                  {['Journal Odoo', 'Journal PennyLane', 'Auto-détection', ''].map(h => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide"
                      style={{ color: 'var(--text-muted)' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {odooJournaux.map((odooNom, i) => {
                  const currentId = localMapping[odooNom] || '';
                  const auto = autoMatch(odooNom);
                  const isMapped = Boolean(currentId);

                  // Options disponibles pour ce journal :
                  // - journaux non attribués à d'autres journaux Odoo
                  // - + le journal actuellement sélectionné pour ce row (pour qu'il reste visible)
                  const availableOptions = plJournaux.filter(j =>
                    !usedPlIds.has(String(j.id)) || String(j.id) === currentId
                  );

                  return (
                    <tr key={odooNom}
                      style={{ borderBottom: i < odooJournaux.length - 1 ? '1px solid var(--border-light)' : 'none' }}>
                      {/* Journal Odoo */}
                      <td className="px-4 py-3">
                        <span className="font-semibold" style={{ color: 'var(--text)' }}>{odooNom}</span>
                      </td>

                      {/* Sélecteur PennyLane */}
                      <td className="px-4 py-3">
                        <select
                          data-testid={`mapping-pl-select-${odooNom}`}
                          value={currentId}
                          onChange={e => setRow(odooNom, e.target.value)}
                          className="text-sm px-3 py-1.5 rounded-lg border focus:outline-none w-full max-w-xs"
                          style={{
                            borderColor: isMapped ? 'var(--primary-solid)' : 'var(--border)',
                            color: 'var(--text)',
                            background: isMapped ? 'var(--primary-50)' : 'white',
                          }}
                          disabled={plJournaux.length === 0}>
                          <option value="">— Automatique —</option>
                          {availableOptions.map(j => (
                            <option key={j.id} value={String(j.id)}>
                              {j.code ? `${j.code} — ` : ''}{j.label || j.name || `ID ${j.id}`}
                              {j.type ? ` (${PL_TYPE_LABEL[j.type] || j.type})` : ''}
                            </option>
                          ))}
                        </select>
                      </td>

                      {/* Badge auto-détection */}
                      <td className="px-4 py-3">
                        {auto ? (
                          <span className="flex items-center gap-1 text-xs px-2 py-0.5 rounded-full w-fit"
                            style={{ background: '#F0F9FF', color: '#0369A1' }}>
                            <CheckCircle2 size={10} />
                            {auto.code ? `${auto.code} — ` : ''}{auto.label || auto.name || `ID ${auto.id}`}
                          </span>
                        ) : (
                          <span className="text-xs" style={{ color: 'var(--text-disabled)' }}>
                            Fallback misc/général
                          </span>
                        )}
                      </td>

                      {/* Bouton reset */}
                      <td className="px-4 py-3">
                        {isMapped && (
                          <button onClick={() => resetRow(odooNom)} title="Supprimer le mapping manuel"
                            className="p-1.5 rounded-lg hover:bg-red-50 transition-colors"
                            style={{ color: '#DC2626' }}>
                            <Link2Off size={14} />
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Bouton save en bas si dirty */}
          {isDirty && (
            <div className="flex justify-end">
              <button onClick={handleSave} disabled={saving} data-testid="mapping-save-btn-bottom"
                className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold text-white transition-all hover:opacity-90 disabled:opacity-50"
                style={{ background: 'var(--primary-gradient)' }}>
                {saving ? <Loader2 size={13} className="animate-spin" /> : <Link2 size={13} />}
                Enregistrer le mapping
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ─── Onglet Envoi PennyLane ────────────────────────────────────────────────────
function PennyLaneTab({ configs }) {
  const [societeId, setSocieteId] = useState(null);
  const [stats, setStats] = useState(null);
  const [plJournaux, setPlJournaux] = useState([]);
  const [sending, setSending] = useState(false);
  const [filterJournal, setFilterJournal] = useState('');
  const [ecritures, setEcritures] = useState([]);
  const [loadingEc, setLoadingEc] = useState(false);
  const [page, setPage] = useState(1);
  const [totalEc, setTotalEc] = useState(0);
  const [filterStatut, setFilterStatut] = useState('all'); // all | pending | sent | error
  const [filterDateDebut, setFilterDateDebut] = useState('');
  const [filterDateFin, setFilterDateFin] = useState('');
  const [trimZeros, setTrimZeros] = useState(false); // supprimer zéros finaux du compte PCG
  const [errorModal, setErrorModal] = useState(null); // texte d'erreur à afficher en modal
  const [syncingTiers, setSyncingTiers] = useState(false);
  const [tiersResult, setTiersResult] = useState(null); // résultat du dernier sync tiers

  // Auto-sélection première société
  useEffect(() => {
    if (!societeId && configs.length > 0) setSocieteId(configs[0].odoo_company_id);
  }, [configs]); // eslint-disable-line react-hooks/exhaustive-deps

  /** Supprime les zéros finaux d'un code PCG. Ex: 411100000 → 4111 */
  const trimCode = (code) => code ? code.replace(/0+$/, '') || code : code;

  const loadStats = useCallback(async () => {
    try {
      const p = { odoo_company_id: societeId };
      if (filterDateDebut) p.date_debut = filterDateDebut;
      if (filterDateFin)   p.date_fin   = filterDateFin;
      const r = await api.tcPlGetStats(p);
      setStats(r.data);
    } catch { setStats(null); }
  }, [societeId, filterDateDebut, filterDateFin]);

  const loadPlJournaux = useCallback(async () => {
    try {
      const r = await api.tcPlGetJournaux();
      setPlJournaux(r.data.journaux || []);
    } catch { /* silencieux si PL non configuré */ }
  }, []);

  const loadEcritures = useCallback(async () => {
    if (!societeId) { setEcritures([]); setTotalEc(0); return; }
    setLoadingEc(true);
    try {
      const params = { odoo_company_id: societeId, per_page: 100, page };
      if (filterJournal)    params.journal_nom = filterJournal;
      if (filterDateDebut)  params.date_debut  = filterDateDebut;
      if (filterDateFin)    params.date_fin    = filterDateFin;
      const r = await api.tcGetEcritures(params);
      let data = r.data.data || [];
      // Filtrer par statut PL côté front
      if (filterStatut === 'sent')    data = data.filter(e => e.pl_sent_at);
      if (filterStatut === 'pending') data = data.filter(e => !e.pl_sent_at && !e.pl_error);
      if (filterStatut === 'error')   data = data.filter(e => e.pl_error && !e.pl_sent_at);
      setEcritures(data);
      setTotalEc(r.data.total || 0);
    } catch { toast.error('Erreur chargement écritures'); }
    finally { setLoadingEc(false); }
  }, [societeId, filterJournal, filterStatut, filterDateDebut, filterDateFin, page]);

  useEffect(() => { loadStats(); loadEcritures(); }, [loadStats, loadEcritures]);
  useEffect(() => { loadPlJournaux(); }, [loadPlJournaux]);

  const handleEnvoyer = async (limit) => {
    if (!societeId) { toast.error('Sélectionnez une société'); return; }
    if (!filterDateDebut || !filterDateFin) {
      toast.warning('Renseignez une date de début et une date de fin avant d\'envoyer');
      return;
    }
    if (sending) return;
    setSending(true);
    try {
      const payload = { odoo_company_id: societeId, limit, trim_zeros: trimZeros };
      if (filterJournal)   payload.journal_nom = filterJournal;
      if (filterDateDebut) payload.date_debut  = filterDateDebut;
      if (filterDateFin)   payload.date_fin    = filterDateFin;
      const r = await api.tcPlEnvoyer(payload);
      const res = r.data.results;
      if (res) {
        toast.success(`PennyLane : ${res.sent} envoyée(s), ${res.errors} erreur(s), ${res.skipped} ignorée(s) sur ${res.total_moves} move(s)`);
      }
      loadStats();
      loadEcritures();
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || 'Erreur lors de l\'envoi';
      toast.error(msg);
    } finally { setSending(false); }
  };

  const handleResetErrors = async () => {
    if (!societeId) return;
    try {
      const r = await api.tcPlResetErrors({ odoo_company_id: societeId });
      toast.success(`${r.data.reset} erreur(s) réinitialisée(s)`);
      loadStats(); loadEcritures();
    } catch { toast.error('Erreur lors de la réinitialisation'); }
  };

  const handleSyncTiers = async () => {
    if (!societeId) { toast.error('Sélectionnez une société'); return; }
    if (syncingTiers) return;
    setSyncingTiers(true);
    setTiersResult(null);
    try {
      const payload = { odoo_company_id: societeId };
      if (filterJournal)   payload.journal_nom = filterJournal;
      if (filterDateDebut) payload.date_debut  = filterDateDebut;
      if (filterDateFin)   payload.date_fin    = filterDateFin;
      const r = await api.tcPlSyncTiers(payload);
      const res = r.data.results;
      setTiersResult(res);
      const msg = `Tiers : ${res.customers_created} client(s) + ${res.suppliers_created} fourn. créé(s), ${res.skipped} ignoré(s)`;
      if (res.errors > 0) toast.warning(`${msg}, ${res.errors} erreur(s)`);
      else toast.success(msg);
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || 'Erreur sync tiers';
      toast.error(msg);
    } finally { setSyncingTiers(false); }
  };

  const pendingMoves = stats?.moves?.pending ?? 0;
  const sentMoves    = stats?.moves?.sent ?? 0;
  const errorMoves   = stats?.moves?.errors ?? 0;
  const totalMoves   = stats?.moves?.total ?? 0;

  return (
    <div className="space-y-5">
      {/* Sélecteur société + contrôles */}
      <div className="flex items-center gap-3 flex-wrap">
        <SocieteSelect value={societeId} societes={configs} onChange={v => { setSocieteId(v); setPage(1); }} />
        <select
          value={filterJournal} onChange={e => setFilterJournal(e.target.value)}
          className="text-sm px-3 py-2 rounded-xl border bg-white focus:outline-none"
          style={{ borderColor: 'var(--border)', color: 'var(--text)' }}>
          <option value="">Tous les journaux</option>
          {(stats?.journaux_pending || []).map(j => <option key={j} value={j}>{j}</option>)}
        </select>
        <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl border bg-white" style={{ borderColor: 'var(--border)' }}>
          <Calendar size={13} style={{ color: 'var(--text-muted)' }} />
          <input type="date" value={filterDateDebut} onChange={e => { setFilterDateDebut(e.target.value); setPage(1); }}
            className="text-sm focus:outline-none bg-transparent" style={{ color: 'var(--text)', width: 130 }}
            title="Date début" />
          <span className="text-xs" style={{ color: 'var(--text-muted)' }}>→</span>
          <input type="date" value={filterDateFin} onChange={e => { setFilterDateFin(e.target.value); setPage(1); }}
            className="text-sm focus:outline-none bg-transparent" style={{ color: 'var(--text)', width: 130 }}
            title="Date fin" />
          {(filterDateDebut || filterDateFin) && (
            <button onClick={() => { setFilterDateDebut(''); setFilterDateFin(''); setPage(1); }}
              className="ml-1 hover:opacity-70 transition-opacity" title="Effacer les dates">
              <X size={12} style={{ color: 'var(--text-muted)' }} />
            </button>
          )}
        </div>
        <div className="flex-1" />
        {/* Toggle trim zéros */}
        <label className="flex items-center gap-2 cursor-pointer select-none" title="Supprime les zéros de fin du code PCG avant envoi à PennyLane (ex: 411100000 → 4111)">
          <div
            onClick={() => setTrimZeros(t => !t)}
            data-testid="pl-trim-zeros-toggle"
            className="relative w-9 h-5 rounded-full transition-colors"
            style={{ background: trimZeros ? 'var(--primary-solid)' : '#D1D5DB', cursor: 'pointer' }}>
            <span className="absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform"
              style={{ transform: trimZeros ? 'translateX(16px)' : 'translateX(0)' }} />
          </div>
          <span className="text-xs font-medium" style={{ color: 'var(--text-muted)' }}>
            Trim zéros ({trimZeros ? 'actif' : 'inactif'})
          </span>
        </label>
        {errorMoves > 0 && (
          <button onClick={handleResetErrors} data-testid="pl-reset-errors"
            className="flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-semibold border transition-all hover:bg-red-50"
            style={{ borderColor: '#FCA5A5', color: '#DC2626' }}>
            <RotateCcw size={13} />Réessayer erreurs ({errorMoves})
          </button>
        )}
        {/* Bouton sync tiers */}
        <button onClick={handleSyncTiers} disabled={syncingTiers || !societeId} data-testid="pl-sync-tiers"
          className="flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-semibold border transition-all hover:bg-indigo-50 disabled:opacity-40"
          style={{ borderColor: '#A5B4FC', color: '#4F46E5' }}
          title="Synchroniser les clients/fournisseurs référencés dans les écritures vers PennyLane">
          {syncingTiers ? <Loader2 size={13} className="animate-spin" /> : <ArrowLeftRight size={13} />}
          Sync tiers
        </button>
        <button onClick={() => handleEnvoyer(20)} disabled={sending || pendingMoves === 0} data-testid="pl-envoyer-test"
          className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold border transition-all hover:bg-gray-50 disabled:opacity-40"
          style={{ borderColor: 'var(--border)', color: 'var(--text)' }}>
          {sending ? <Loader2 size={13} className="animate-spin" /> : <Zap size={13} />}
          Test (20 moves)
        </button>
        <button onClick={() => handleEnvoyer(0)} disabled={sending || pendingMoves === 0 || !filterDateDebut || !filterDateFin} data-testid="pl-envoyer-tout"
          className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold text-white transition-all hover:opacity-90 disabled:opacity-40"
          title={(!filterDateDebut || !filterDateFin) ? 'Renseignez une date de début et une date de fin' : `Envoyer les ${pendingMoves} move(s) en attente vers PennyLane`}
          style={{ background: 'var(--primary-gradient)' }}>
          {sending ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />}
          Envoyer ({pendingMoves})
        </button>
      </div>

      {/* Bandeau d'envoi en cours */}
      {sending && (
        <div className="flex items-center gap-2 px-4 py-3 rounded-xl text-sm"
          style={{ background: 'var(--primary-50)', color: 'var(--primary-solid)', border: '1px solid var(--primary-200)' }}>
          <Loader2 size={15} className="animate-spin" />
          <span>Envoi en cours vers PennyLane… Patientez, chaque écriture est vérifiée et envoyée.</span>
        </div>
      )}

      {/* Bandeau sync tiers en cours */}
      {syncingTiers && (
        <div className="flex items-center gap-2 px-4 py-3 rounded-xl text-sm"
          style={{ background: '#EEF2FF', color: '#4F46E5', border: '1px solid #C7D2FE' }}>
          <Loader2 size={15} className="animate-spin" />
          <span>Synchronisation des tiers vers PennyLane en cours…</span>
        </div>
      )}

      {/* Résultat du dernier sync tiers */}
      {tiersResult && !syncingTiers && (
        <div className="flex items-center justify-between px-4 py-3 rounded-xl text-sm"
          style={{ background: tiersResult.errors > 0 ? '#FFFBEB' : '#F0FDF4',
                   color: tiersResult.errors > 0 ? '#92400E' : '#166534',
                   border: `1px solid ${tiersResult.errors > 0 ? '#FCD34D' : '#BBF7D0'}` }}>
          <span>
            Tiers synchronisés — {tiersResult.customers_created} client(s) + {tiersResult.suppliers_created} fournisseur(s) créé(s),
            {' '}{tiersResult.skipped} ignoré(s){tiersResult.errors > 0 ? `, ${tiersResult.errors} erreur(s)` : ''}
          </span>
          <button onClick={() => setTiersResult(null)} className="ml-4 hover:opacity-60"><X size={13} /></button>
        </div>
      )}

      {/* Statistiques */}
      {stats && (
        <div className="grid grid-cols-4 gap-4">
          {[
            { label: 'Total moves', value: totalMoves,   color: '#6B7280', bg: '#F3F4F6', statut: 'all'     },
            { label: 'Envoyés',     value: sentMoves,    color: '#166534', bg: '#F0FDF4', statut: 'sent',    icon: CheckCircle2 },
            { label: 'En attente',  value: pendingMoves, color: '#1D4ED8', bg: '#EFF6FF', statut: 'pending', icon: Clock },
            { label: 'Erreurs',     value: errorMoves,   color: '#DC2626', bg: '#FEF2F2', statut: 'error',   icon: XCircle },
          ].map(s => {
            const isActive = filterStatut === s.statut;
            return (
              <button
                key={s.label}
                data-testid={`pl-card-${s.statut}`}
                onClick={() => { setFilterStatut(isActive ? 'all' : s.statut); setPage(1); }}
                className="rounded-xl border p-4 text-left w-full transition-all hover:shadow-md"
                style={{
                  background: isActive ? s.bg : 'white',
                  borderColor: isActive ? s.color : 'var(--border)',
                  borderWidth: isActive ? 2 : 1,
                  cursor: 'pointer',
                  outline: 'none',
                  transform: isActive ? 'translateY(-1px)' : 'none',
                }}>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs font-semibold uppercase tracking-wide" style={{ color: isActive ? s.color : 'var(--text-muted)' }}>{s.label}</span>
                  {s.icon && <s.icon size={14} style={{ color: s.color }} />}
                </div>
                <div className="text-2xl font-bold" style={{ color: s.color,  }}>
                  {s.value?.toLocaleString('fr-FR') ?? '—'}
                </div>
                {totalMoves > 0 && (
                  <div className="mt-2 h-1.5 rounded-full overflow-hidden" style={{ background: '#E5E7EB' }}>
                    <div className="h-full rounded-full transition-all" style={{ background: s.color, width: `${Math.round((s.value / totalMoves) * 100)}%` }} />
                  </div>
                )}
              </button>
            );
          })}
        </div>
      )}

      {/* Journaux PennyLane disponibles */}
      {plJournaux.length > 0 && (
        <details className="bg-white rounded-xl border p-3 text-xs" style={{ borderColor: 'var(--border)' }}>
          <summary className="cursor-pointer font-semibold" style={{ color: 'var(--text-muted)' }}>
            Journaux PennyLane disponibles ({plJournaux.length})
          </summary>
          <div className="flex flex-wrap gap-2 mt-2">
            {plJournaux.map(j => (
              <span key={j.id} className="px-2 py-1 rounded-lg font-mono"
                style={{ background: '#F3F4F6', color: '#374151' }}>
                {j.code} — {j.label || j.name || ''}
              </span>
            ))}
          </div>
        </details>
      )}

      {/* Tableau des écritures */}
      {loadingEc ? (
        <div className="flex justify-center py-16">
          <Loader2 className="animate-spin" size={24} style={{ color: 'var(--primary-solid)' }} />
        </div>
      ) : ecritures.length === 0 ? (
        <div className="bg-white rounded-xl border p-12 text-center" style={{ borderColor: 'var(--border)' }}>
          <Send size={36} className="mx-auto mb-3" style={{ color: 'var(--text-disabled)' }} />
          <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
            {societeId ? 'Aucune écriture pour ce filtre' : 'Sélectionnez une société pour commencer'}
          </p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border overflow-hidden" style={{ borderColor: 'var(--border)' }}>
          <div className="px-4 py-2 text-xs" style={{ background: '#FAFAFA', borderBottom: '1px solid var(--border)', color: 'var(--text-muted)' }}>
            {ecritures.length} écriture(s) affichée(s) — {totalEc} en base
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr style={{ background: '#FAFAFA', borderBottom: '1px solid var(--border)' }}>
                  {['Date', 'Journal', 'Pièce', 'Compte PCG', 'Libellé', 'Débit', 'Crédit', 'Statut PL'].map(h => (
                    <th key={h} className="px-3 py-2.5 text-left font-semibold uppercase tracking-wide whitespace-nowrap"
                      style={{ color: 'var(--text-muted)' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {ecritures.map((e, i) => {
                  const isSent  = Boolean(e.pl_sent_at);
                  const isError = Boolean(e.pl_error) && !isSent;
                  return (
                    <tr key={e.tc_ecr_id}
                      style={{ borderBottom: i < ecritures.length - 1 ? '1px solid var(--border-light)' : 'none' }}>
                      <td className="px-3 py-2 whitespace-nowrap" style={{ color: 'var(--text-muted)' }}>{fmtDate(e.date)}</td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        <span className="px-1.5 py-0.5 rounded font-semibold" style={{ background: '#F3F4F6', color: '#6B7280' }}>{e.journal_nom}</span>
                      </td>
                      <td className="px-3 py-2 font-mono font-semibold whitespace-nowrap" style={{ color: 'var(--text)' }}>{e.move_nom}</td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        <span className="font-mono font-semibold" style={{ color: 'var(--primary-solid)' }}>
                          {trimZeros ? trimCode(e.compte_code) : e.compte_code}
                        </span>
                        {trimZeros && e.compte_code !== trimCode(e.compte_code) && (
                          <span className="ml-1 text-xs line-through opacity-30" style={{ color: 'var(--text-muted)' }}>{e.compte_code}</span>
                        )}
                        <span className="ml-1 opacity-60">{e.compte_nom?.split(' ').slice(0, 2).join(' ')}</span>
                      </td>
                      <td className="px-3 py-2 max-w-xs truncate" style={{ color: 'var(--text)' }} title={e.libelle}>{e.libelle}</td>
                      <td className="px-3 py-2 text-right font-semibold whitespace-nowrap" style={{ color: e.debit > 0 ? '#166534' : 'transparent' }}>
                        {e.debit > 0 ? fmt(e.debit) : ''}
                      </td>
                      <td className="px-3 py-2 text-right font-semibold whitespace-nowrap" style={{ color: e.credit > 0 ? '#DC2626' : 'transparent' }}>
                        {e.credit > 0 ? fmt(e.credit) : ''}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        {isSent ? (
                          <div className="flex items-center gap-1.5">
                            <CheckCircle2 size={12} style={{ color: '#166534' }} />
                            <span className="px-1.5 py-0.5 rounded text-xs font-semibold" style={{ background: '#F0FDF4', color: '#166534' }}>
                              Envoyée
                            </span>
                          </div>
                        ) : isError ? (
                          <button
                            className="flex items-center gap-1.5 cursor-pointer hover:opacity-80 transition-opacity text-left"
                            title="Cliquer pour voir l'erreur complète"
                            onClick={() => setErrorModal(e.pl_error)}
                          >
                            <XCircle size={12} style={{ color: '#DC2626' }} />
                            <span className="px-1.5 py-0.5 rounded text-xs font-semibold" style={{ background: '#FEF2F2', color: '#DC2626' }}>
                              Erreur (voir détail)
                            </span>
                          </button>
                        ) : (
                          <div className="flex items-center gap-1.5">
                            <Clock size={12} style={{ color: '#1D4ED8' }} />
                            <span className="px-1.5 py-0.5 rounded text-xs font-semibold" style={{ background: '#EFF6FF', color: '#1D4ED8' }}>
                              En attente
                            </span>
                          </div>
                        )}
                        {isSent && e.pl_ledger_entry_id && (
                          <div className="font-mono text-xs mt-0.5 opacity-50">#{e.pl_ledger_entry_id}</div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── Modale détail erreur PennyLane ── */}
      {errorModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center"
          style={{ background: 'rgba(0,0,0,0.55)' }}
          onClick={() => setErrorModal(null)}
        >
          <div
            className="rounded-xl shadow-2xl p-6 max-w-2xl w-full mx-4 flex flex-col gap-4"
            style={{ background: 'var(--card)', border: '1px solid var(--border)' }}
            onClick={e => e.stopPropagation()}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <XCircle size={18} style={{ color: '#DC2626' }} />
                <span className="font-semibold text-sm" style={{ color: 'var(--text)' }}>Détail de l'erreur PennyLane</span>
              </div>
              <button onClick={() => setErrorModal(null)} className="p-1 rounded hover:opacity-70 transition-opacity">
                <X size={16} style={{ color: 'var(--text-muted)' }} />
              </button>
            </div>
            <pre
              className="rounded-lg p-4 text-xs font-mono overflow-auto max-h-80 whitespace-pre-wrap break-all"
              style={{ background: '#FEF2F2', color: '#991B1B', border: '1px solid #FECACA' }}
            >
              {errorModal}
            </pre>
            <div className="flex justify-end gap-2">
              <button
                className="px-4 py-2 rounded-lg text-sm font-medium transition-colors hover:opacity-80"
                style={{ background: 'var(--border)', color: 'var(--text)' }}
                onClick={() => setErrorModal(null)}
              >
                Fermer
              </button>
              <button
                className="px-4 py-2 rounded-lg text-sm font-medium transition-colors hover:opacity-80"
                style={{ background: '#DC2626', color: '#fff' }}
                onClick={() => {
                  navigator.clipboard.writeText(errorModal).then(() => toast.success('Erreur copiée'));
                }}
              >
                Copier
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Page principale ───────────────────────────────────────────────────────────
export default function TransfertComptable() {
  const [activeTab, setActiveTab] = useState('config');
  const [configs, setConfigs] = useState([]);

  const loadConfigs = useCallback(async () => {
    try {
      const r = await api.tcGetStats();
      setConfigs(r.data || []);
    } catch {}
  }, []);

  useEffect(() => { loadConfigs(); }, [loadConfigs]);

  const TABS = [
    { key: 'config',     label: 'Configuration',        icon: Settings2      },
    { key: 'plan',       label: 'Plan comptable',        icon: BookOpen       },
    { key: 'journaux',   label: 'Journaux',              icon: Filter         },
    { key: 'grandlivre', label: 'Grand livre',           icon: TrendingUp     },
    { key: 'clients',    label: 'Factures clients',      icon: Receipt        },
    { key: 'fourn',      label: 'Factures fournisseurs', icon: FileText       },
    { key: 'mapping',    label: 'Mapping journaux',      icon: Link2          },
    { key: 'pennylane',  label: 'Envoi PennyLane',       icon: Send           },
  ];

  return (
    <div className="p-6 space-y-6" style={{  }}>
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl flex items-center justify-center"
          style={{ background: 'var(--primary-gradient)' }}>
          <ArrowLeftRight size={18} color="white" />
        </div>
        <div>
          <h1 className="text-2xl font-bold" style={{ color: 'var(--text)',  }}>
            Transfert Comptable
          </h1>
          <p className="text-sm mt-0.5" style={{ color: 'var(--text-muted)' }}>
            Extraction Odoo → audit Bedee → injection PennyLane (Phase 2)
          </p>
        </div>
        {configs.length > 0 && (
          <div className="ml-auto flex gap-4 text-center">
            <div className="bg-white rounded-xl px-4 py-2 border" style={{ borderColor: 'var(--border)' }}>
              <div className="text-lg font-bold" style={{ color: 'var(--primary-solid)',  }}>{configs.length}</div>
              <div className="text-xs" style={{ color: 'var(--text-muted)' }}>Société(s)</div>
            </div>
            <div className="bg-white rounded-xl px-4 py-2 border" style={{ borderColor: 'var(--border)' }}>
              <div className="text-lg font-bold" style={{ color: 'var(--text)',  }}>{configs.reduce((s, c) => s + (c.nb_ecritures || 0), 0).toLocaleString('fr-FR')}</div>
              <div className="text-xs" style={{ color: 'var(--text-muted)' }}>Écritures</div>
            </div>
            <div className="bg-white rounded-xl px-4 py-2 border" style={{ borderColor: 'var(--border)' }}>
              <div className="text-lg font-bold" style={{ color: 'var(--text)',  }}>{configs.reduce((s, c) => s + (c.nb_factures_clients || 0) + (c.nb_factures_fournisseurs || 0), 0).toLocaleString('fr-FR')}</div>
              <div className="text-xs" style={{ color: 'var(--text-muted)' }}>Factures</div>
            </div>
          </div>
        )}
      </div>

      {/* Onglets */}
      <div className="flex gap-1 p-1 rounded-xl" style={{ background: '#F3F4F6' }}>
        {TABS.map(t => (
          <button key={t.key} onClick={() => setActiveTab(t.key)}
            data-testid={`tc-tab-${t.key}`}
            className="flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold transition-all flex-1 justify-center"
            style={activeTab === t.key
              ? { background: 'white', color: 'var(--text)', boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }
              : { color: 'var(--text-muted)' }}>
            <t.icon size={14} />
            {t.label}
          </button>
        ))}
      </div>

      {/* Contenu */}
      <div>
        {activeTab === 'config'     && <ConfigTab configs={configs} onRefresh={loadConfigs} />}
        {activeTab === 'plan'       && <PlanComptableTab configs={configs} />}
        {activeTab === 'journaux'   && <JournauxTab configs={configs} />}
        {activeTab === 'grandlivre' && <GrandLivreTab configs={configs} />}
        {activeTab === 'clients'    && <FacturesTab configs={configs} moveType="out_invoice" />}
        {activeTab === 'fourn'      && <FacturesTab configs={configs} moveType="in_invoice" />}
        {activeTab === 'mapping'    && <MappingJournauxTab configs={configs} />}
        {activeTab === 'pennylane'  && <PennyLaneTab configs={configs} />}
      </div>
    </div>
  );
}
